import { BEAAttribution } from '../../../components/BEAAttribution';
import { type StepForm, WizardField } from '../WizardField';

interface Props {
  form: StepForm;
}

export function Step7Assumptions({ form }: Props) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-800">
          RIMS II Multipliers & Assumptions
        </h3>
        <p className="mt-0.5 text-sm text-slate-500">
          Operations and construction multipliers from your uploaded RIMS II dataset. Assumption
          defaults follow <em>docs/specs/economic-impact-formulas.md</em>.
        </p>
        <BEAAttribution variant="rims" className="mt-2" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <p className="col-span-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Operations multipliers (Type II direct-effect)
        </p>

        <WizardField
          form={form}
          name="step7.operationsEmploymentMultiplier"
          label="Employment multiplier (jobs per direct job)"
          type="number"
          min={1}
          step={0.01}
          hint="From BEA DE Employment column"
        />

        <WizardField
          form={form}
          name="step7.operationsEarningsMultiplier"
          label="Earnings multiplier ($ per $ direct earnings)"
          type="number"
          min={1}
          step={0.01}
          hint="From BEA DE Earnings column"
        />

        <p className="col-span-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Construction multipliers (final-demand, NAICS 236220)
        </p>

        <WizardField
          form={form}
          name="step7.constructionEmploymentMultiplierJobsPerMillion"
          label="Employment multiplier (jobs per $1M)"
          type="number"
          min={0}
          step={0.1}
          hint="From BEA FD Employment column"
        />

        <WizardField
          form={form}
          name="step7.constructionEarningsMultiplier"
          label="Earnings multiplier ($ per $ demand)"
          type="number"
          min={0}
          step={0.01}
          hint="From BEA FD Earnings column"
        />

        <WizardField
          form={form}
          name="step7.constructionOutputMultiplier"
          label="Output multiplier ($ per $ demand)"
          type="number"
          min={0}
          step={0.01}
          hint="From BEA FD Output column"
        />

        <p className="col-span-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Local spending assumptions
        </p>

        <WizardField
          form={form}
          name="step7.localConstructionCaptureRate"
          label="Local construction capture rate"
          format="percent"
          min={0}
          max={1}
          step={0.05}
          hint="Fraction of construction spend in local economy"
        />

        <WizardField
          form={form}
          name="step7.workerLocalCaptureRate"
          label="Worker local capture rate"
          format="percent"
          min={0}
          max={1}
          step={0.05}
          hint="Fraction of worker earnings spent locally"
        />

        <WizardField
          form={form}
          name="step7.workerTaxableSpendingShare"
          label="Worker taxable spending share"
          format="percent"
          min={0}
          max={1}
          step={0.05}
          hint="Fraction of local worker spend that is taxable"
        />

        <WizardField
          form={form}
          name="step7.materialsShareOfConstruction"
          label="Materials share of construction"
          format="percent"
          min={0}
          max={1}
          step={0.05}
          hint="Fraction of construction spend that is materials"
        />

        <WizardField
          form={form}
          name="step7.materialsTaxabilityRate"
          label="Materials taxability rate"
          format="percent"
          min={0}
          max={1}
          step={0.05}
          hint="Fraction of materials subject to local sales tax"
        />

        <WizardField
          form={form}
          name="step7.permitFeeRate"
          label="Permit & fee rate"
          format="percent"
          min={0}
          max={0.1}
          step={0.001}
          hint="As a fraction of construction spending"
        />

        <p className="col-span-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Community impact assumptions
        </p>

        <WizardField
          form={form}
          name="step7.displacementRate"
          label="Displacement rate"
          format="percent"
          min={0}
          max={1}
          step={0.05}
          hint="Fraction of new activity displacing existing"
        />

        <WizardField
          form={form}
          name="step7.workerRelocationPct"
          label="Worker relocation rate"
          format="percent"
          min={0}
          max={1}
          step={0.05}
          hint="Fraction of workers who move to the area"
        />

        <WizardField
          form={form}
          name="step7.averageHouseholdSize"
          label="Average household size (persons)"
          type="number"
          min={1}
          max={10}
          step={0.1}
        />

        <WizardField
          form={form}
          name="step7.schoolAgeChildrenPerHousehold"
          label="School-age children per household"
          type="number"
          min={0}
          max={5}
          step={0.1}
        />
      </div>
    </div>
  );
}
